// ============================================
// Resend Email Service - Notification System
// ============================================

import { Resend } from "resend";
import { DomainItem, AIAnalysisResult } from "../types";

export class ResendService {
  private client: Resend | null = null;
  private apiKey: string;
  private fromEmail: string = "noreply@zerolister.com";

  constructor(apiKey: string) {
    this.apiKey = apiKey;
    if (this.apiKey) {
      this.client = new Resend(this.apiKey);
    }
  }

  updateApiKey(key: string) {
    this.apiKey = key;
    if (this.apiKey) {
      this.client = new Resend(this.apiKey);
    } else {
      this.client = null;
    }
  }

  isConfigured(): boolean {
    return !!this.client;
  }

  /**
   * Send alert for high-value domain found
   */
  async sendDomainAlert(
    to: string,
    domain: DomainItem,
    analysis?: AIAnalysisResult
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.client) {
      return { success: false, error: "Resend not configured" };
    }

    try {
      const verdictColor = analysis?.verdict === "Top Pick" ? "#22c55e" :
                          analysis?.verdict === "Pass" ? "#ef4444" : "#eab308";

      await this.client.emails.send({
        from: this.fromEmail,
        to: to,
        subject: `🔥 Domain Alert: ${domain.name} - $${domain.price}`,
        html: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #1e40af; margin-bottom: 20px;">Zero Point Domain Alert</h1>

            <div style="background: #f8fafc; border-radius: 12px; padding: 20px; margin-bottom: 20px;">
              <h2 style="margin: 0 0 10px 0; color: #1f2937;">${domain.name}</h2>
              <p style="margin: 5px 0; color: #6b7280;">
                <strong>Current Price:</strong> <span style="color: #22c55e; font-size: 1.25em;">$${domain.price}</span>
              </p>
              <p style="margin: 5px 0; color: #6b7280;">
                <strong>Bidders:</strong> ${domain.bidders}
              </p>
              <p style="margin: 5px 0; color: #6b7280;">
                <strong>Ends:</strong> ${new Date(domain.endTime).toLocaleString()}
              </p>
              <p style="margin: 5px 0; color: #6b7280;">
                <strong>Type:</strong> ${domain.status}
              </p>
            </div>

            ${analysis ? `
            <div style="background: #f0fdf4; border-radius: 12px; padding: 20px; margin-bottom: 20px; border-left: 4px solid ${verdictColor};">
              <h3 style="margin: 0 0 10px 0; color: #166534;">AI Analysis</h3>
              <p style="margin: 5px 0;">
                <strong>Verdict:</strong>
                <span style="background: ${verdictColor}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 0.875em;">
                  ${analysis.verdict}
                </span>
              </p>
              <p style="margin: 5px 0;"><strong>Valuation:</strong> ${analysis.valuation}</p>
              <p style="margin: 10px 0; color: #374151;">${analysis.analysis}</p>
              ${analysis.legal_relevance ? `<p style="margin: 5px 0; color: #6b7280;"><strong>Legal Relevance:</strong> ${analysis.legal_relevance}</p>` : ''}
            </div>
            ` : ''}

            <a href="https://www.dropcatch.com/domain/${domain.name}"
               style="display: inline-block; background: #1e40af; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600;">
              View on DropCatch →
            </a>

            <p style="margin-top: 30px; color: #9ca3af; font-size: 0.875em;">
              Zero Point Domains - Professional DropCatch Domain Acquisition Platform
            </p>
          </div>
        `,
      });

      return { success: true };
    } catch (error) {
      console.error("Email send error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send email"
      };
    }
  }

  /**
   * Send daily digest of top domains
   */
  async sendDailyDigest(
    to: string,
    domains: DomainItem[]
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.client) {
      return { success: false, error: "Resend not configured" };
    }

    const topPicks = domains.filter(d => d.aiAnalysis?.verdict === "Top Pick");
    const maybes = domains.filter(d => d.aiAnalysis?.verdict === "Hand Register" || d.aiAnalysis?.verdict === "Backorder");

    try {
      await this.client.emails.send({
        from: this.fromEmail,
        to: to,
        subject: `📊 Daily Domain Digest - ${topPicks.length} Top Picks`,
        html: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #1e40af; margin-bottom: 20px;">Daily Domain Digest</h1>

            <div style="display: flex; gap: 10px; margin-bottom: 20px;">
              <div style="background: #dcfce7; padding: 15px; border-radius: 8px; flex: 1; text-align: center;">
                <div style="font-size: 2em; font-weight: bold; color: #166534;">${topPicks.length}</div>
                <div style="color: #166534;">Top Picks</div>
              </div>
              <div style="background: #fef9c3; padding: 15px; border-radius: 8px; flex: 1; text-align: center;">
                <div style="font-size: 2em; font-weight: bold; color: #854d0e;">${maybes.length}</div>
                <div style="color: #854d0e;">Consider</div>
              </div>
              <div style="background: #e0e7ff; padding: 15px; border-radius: 8px; flex: 1; text-align: center;">
                <div style="font-size: 2em; font-weight: bold; color: #3730a3;">${domains.length}</div>
                <div style="color: #3730a3;">Total Scanned</div>
              </div>
            </div>

            ${topPicks.length > 0 ? `
            <h2 style="color: #166534; border-bottom: 2px solid #dcfce7; padding-bottom: 10px;">🔥 Top Picks</h2>
            ${topPicks.slice(0, 10).map(d => `
              <div style="background: #f8fafc; border-radius: 8px; padding: 15px; margin-bottom: 10px;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #1f2937;">${d.name}</strong>
                  <span style="color: #22c55e; font-weight: bold;">$${d.price}</span>
                </div>
                <p style="margin: 5px 0 0 0; color: #6b7280; font-size: 0.875em;">
                  ${d.aiAnalysis?.analysis || 'No analysis'}
                </p>
              </div>
            `).join('')}
            ` : '<p style="color: #6b7280;">No top picks today. Keep scanning!</p>'}

            <a href="https://zeropointdomains.com"
               style="display: inline-block; background: #1e40af; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; margin-top: 20px;">
              Open Dashboard →
            </a>

            <p style="margin-top: 30px; color: #9ca3af; font-size: 0.875em;">
              Zero Point Domains - Professional DropCatch Domain Acquisition Platform
            </p>
          </div>
        `,
      });

      return { success: true };
    } catch (error) {
      console.error("Email send error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send email"
      };
    }
  }

  /**
   * Send auction ending soon alert
   */
  async sendEndingSoonAlert(
    to: string,
    domains: DomainItem[]
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.client) {
      return { success: false, error: "Resend not configured" };
    }

    try {
      await this.client.emails.send({
        from: this.fromEmail,
        to: to,
        subject: `⏰ ${domains.length} Domain Auctions Ending Soon!`,
        html: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #dc2626; margin-bottom: 20px;">⏰ Auctions Ending Soon!</h1>

            <p style="color: #374151; margin-bottom: 20px;">
              The following domain auctions are ending within the next hour:
            </p>

            ${domains.map(d => `
              <div style="background: #fef2f2; border-radius: 8px; padding: 15px; margin-bottom: 10px; border-left: 4px solid #dc2626;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #1f2937;">${d.name}</strong>
                  <span style="color: #22c55e; font-weight: bold;">$${d.price}</span>
                </div>
                <p style="margin: 5px 0 0 0; color: #dc2626; font-size: 0.875em;">
                  Ends: ${new Date(d.endTime).toLocaleString()}
                </p>
                <a href="https://www.dropcatch.com/domain/${d.name}"
                   style="color: #1e40af; font-size: 0.875em;">
                  Bid Now →
                </a>
              </div>
            `).join('')}

            <p style="margin-top: 30px; color: #9ca3af; font-size: 0.875em;">
              Zero Point Domains - Professional DropCatch Domain Acquisition Platform
            </p>
          </div>
        `,
      });

      return { success: true };
    } catch (error) {
      console.error("Email send error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send email"
      };
    }
  }

  /**
   * Send backorder won notification
   */
  async sendBackorderWonAlert(
    to: string,
    domain: string,
    price: number
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.client) {
      return { success: false, error: "Resend not configured" };
    }

    try {
      await this.client.emails.send({
        from: this.fromEmail,
        to: to,
        subject: `🎉 Backorder Won: ${domain}`,
        html: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #22c55e; margin-bottom: 20px;">🎉 Congratulations!</h1>

            <div style="background: #f0fdf4; border-radius: 12px; padding: 20px; margin-bottom: 20px; text-align: center;">
              <h2 style="margin: 0 0 10px 0; color: #166534;">You won the backorder!</h2>
              <p style="font-size: 1.5em; font-weight: bold; color: #1f2937; margin: 10px 0;">${domain}</p>
              <p style="color: #22c55e; font-size: 1.25em;">Final Price: $${price}</p>
            </div>

            <p style="color: #374151;">
              The domain has been secured and will be transferred to your DropCatch account.
              Check your account for transfer instructions.
            </p>

            <a href="https://www.dropcatch.com/account/domains"
               style="display: inline-block; background: #22c55e; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; margin-top: 20px;">
              View in DropCatch →
            </a>

            <p style="margin-top: 30px; color: #9ca3af; font-size: 0.875em;">
              Zero Point Domains - Professional DropCatch Domain Acquisition Platform
            </p>
          </div>
        `,
      });

      return { success: true };
    } catch (error) {
      console.error("Email send error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send email"
      };
    }
  }

  /**
   * Send outbid notification
   */
  async sendOutbidAlert(
    to: string,
    domain: string,
    currentBid: number,
    yourMaxBid: number
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.client) {
      return { success: false, error: "Resend not configured" };
    }

    try {
      await this.client.emails.send({
        from: this.fromEmail,
        to: to,
        subject: `⚠️ Outbid Alert: ${domain}`,
        html: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #dc2626; margin-bottom: 20px;">⚠️ You've Been Outbid!</h1>

            <div style="background: #fef2f2; border-radius: 12px; padding: 20px; margin-bottom: 20px; border-left: 4px solid #dc2626;">
              <h2 style="margin: 0 0 10px 0; color: #991b1b;">${domain}</h2>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-top: 15px;">
                <div>
                  <p style="margin: 0; color: #6b7280; font-size: 0.875em;">Current High Bid</p>
                  <p style="margin: 5px 0; color: #dc2626; font-size: 1.5em; font-weight: bold;">$${currentBid.toLocaleString()}</p>
                </div>
                <div>
                  <p style="margin: 0; color: #6b7280; font-size: 0.875em;">Your Max Bid</p>
                  <p style="margin: 5px 0; color: #6b7280; font-size: 1.5em; font-weight: bold;">$${yourMaxBid.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <p style="color: #374151; margin-bottom: 20px;">
              Someone has placed a higher bid on this domain. Increase your max bid to stay competitive!
            </p>

            <a href="https://www.dropcatch.com/domain/${domain}"
               style="display: inline-block; background: #dc2626; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600;">
              Increase Bid Now →
            </a>

            <p style="margin-top: 30px; color: #9ca3af; font-size: 0.875em;">
              Zero Point Domains - Domain Acquisition Platform
            </p>
          </div>
        `,
      });

      return { success: true };
    } catch (error) {
      console.error("Email send error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send email"
      };
    }
  }

  /**
   * Send snipe warning alert (auction ending soon)
   */
  async sendSnipeAlert(
    to: string,
    domain: string,
    minutesRemaining: number
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.client) {
      return { success: false, error: "Resend not configured" };
    }

    try {
      await this.client.emails.send({
        from: this.fromEmail,
        to: to,
        subject: `🚨 URGENT: ${domain} ends in ${minutesRemaining} minutes!`,
        html: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #dc2626; margin-bottom: 20px; animation: pulse 1s infinite;">
              🚨 SNIPE ALERT!
            </h1>

            <div style="background: #fef2f2; border-radius: 12px; padding: 20px; margin-bottom: 20px; border: 2px solid #dc2626;">
              <h2 style="margin: 0 0 10px 0; color: #991b1b; text-align: center; font-size: 1.5em;">
                ${domain}
              </h2>
              <div style="text-align: center; margin: 20px 0;">
                <p style="margin: 0; color: #dc2626; font-size: 3em; font-weight: bold;">
                  ${minutesRemaining}
                </p>
                <p style="margin: 0; color: #991b1b; font-size: 1.2em;">
                  MINUTES REMAINING
                </p>
              </div>
            </div>

            <p style="color: #374151; margin-bottom: 20px; text-align: center; font-size: 1.1em;">
              This auction is about to end! Take action now to secure this domain.
            </p>

            <div style="text-align: center;">
              <a href="https://www.dropcatch.com/domain/${domain}"
                 style="display: inline-block; background: #dc2626; color: white; padding: 16px 32px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 1.1em;">
                BID NOW →
              </a>
            </div>

            <p style="margin-top: 30px; color: #9ca3af; font-size: 0.875em; text-align: center;">
              Zero Point Domains - Domain Acquisition Platform
            </p>
          </div>
        `,
      });

      return { success: true };
    } catch (error) {
      console.error("Email send error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send email"
      };
    }
  }

  /**
   * Send watchlist keyword match alert
   */
  async sendWatchlistMatchAlert(
    to: string,
    keyword: string,
    domains: Array<{ name: string; price: number; endTime: string }>
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.client) {
      return { success: false, error: "Resend not configured" };
    }

    try {
      await this.client.emails.send({
        from: this.fromEmail,
        to: to,
        subject: `🎯 Watchlist Match: "${keyword}" - ${domains.length} domains found`,
        html: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: #1e40af; margin-bottom: 20px;">🎯 Watchlist Match Found!</h1>

            <div style="background: #eff6ff; border-radius: 12px; padding: 20px; margin-bottom: 20px; border-left: 4px solid #1e40af;">
              <p style="margin: 0; color: #1e40af; font-size: 1.1em;">
                <strong>${domains.length}</strong> new domains matching your keyword:
              </p>
              <p style="margin: 10px 0 0 0; font-size: 1.5em; font-weight: bold; color: #1e3a8a;">
                "${keyword}"
              </p>
            </div>

            <h3 style="color: #374151; margin-bottom: 15px;">Matching Domains:</h3>

            ${domains.slice(0, 10).map(d => `
              <div style="background: #f8fafc; border-radius: 8px; padding: 15px; margin-bottom: 10px; border: 1px solid #e2e8f0;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <strong style="color: #1f2937;">${d.name}</strong>
                  <span style="color: #22c55e; font-weight: bold;">$${d.price.toLocaleString()}</span>
                </div>
                <p style="margin: 5px 0 0 0; color: #6b7280; font-size: 0.875em;">
                  Ends: ${new Date(d.endTime).toLocaleString()}
                </p>
              </div>
            `).join('')}

            ${domains.length > 10 ? `
              <p style="color: #6b7280; font-style: italic;">
                ... and ${domains.length - 10} more matches
              </p>
            ` : ''}

            <a href="https://www.dropcatch.com"
               style="display: inline-block; background: #1e40af; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600; margin-top: 20px;">
              View All Matches →
            </a>

            <p style="margin-top: 30px; color: #9ca3af; font-size: 0.875em;">
              Zero Point Domains - Domain Acquisition Platform
            </p>
          </div>
        `,
      });

      return { success: true };
    } catch (error) {
      console.error("Email send error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to send email"
      };
    }
  }
}
